import * as React from 'react';
import styled from 'styled-components';
import { BsBarChartSteps } from 'react-icons/bs';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
// import calculatePercentOtm from 'utils/shared/trades/calculatePercentOtm';
import DarkPoolLevels from 'components/DarkPoolLevels';
import { TableWrapper, TableCell, HeaderCell, HeaderCellWrapper } from 'components/Table';
import { useModal } from 'hooks/useModal';
import SubscribeModal from 'components/SubscribeModal';
import { hasDashboardAccess } from 'services/client/token';
import { AuthStatus } from 'constants/auth';
import useViewer from 'hooks/useViewer';
import { DarkPoolAggregatedDetails } from './props';

import { DarkPoolOverviewRow, SingleRowBody } from './styles';

const TITLE_NAMES = ['Premium', 'Volume', 'Late Buys', 'Late Sells', 'Golden', 'Levels'];

export const StyledTableHeader = styled.div<{ isDark?: boolean }>`
  background-color: ${({ theme, isDark }) =>
    isDark ? theme.color.backgroundSecondary : theme.color.paper};
`;
export const TableHeader: React.FC<
  React.PropsWithChildren<{ isDark?: boolean; style?: object }>
> = ({ children, isDark, style }) => {
  return (
    <StyledTableHeader
      isDark={isDark}
      className={`flex-grow-1 relative flex-shrink-0 px-3 py-1`}
      style={style}
    >
      {children}
    </StyledTableHeader>
  );
};

const TickerOverviewTable: React.FC<
  React.PropsWithChildren<{
    darkPoolByTicker: DarkPoolAggregatedDetails;
    activeTicker: string;
    hideLevelsColumn?: boolean;
  }>
> = ({ darkPoolByTicker, activeTicker, hideLevelsColumn = false }) => {
  const viewer = useViewer();
  const hasAccess = viewer.status === AuthStatus.User && hasDashboardAccess(viewer);

  const { isOpen: isLevelsOpen, openModal: openLevels, closeModal: closeLevels } = useModal();
  const {
    isOpen: isSubscribeOpen,
    openModal: openSubscribe,
    closeModal: closeSubscribe,
  } = useModal();

  // Memoize the visibleColumns to avoid re-filtering on every render
  const visibleColumns = React.useMemo(
    () => (hideLevelsColumn ? TITLE_NAMES.filter((name) => name !== 'Levels') : TITLE_NAMES),
    [hideLevelsColumn],
  );

  return (
    <>
      <TableWrapper className="min-w-0 max-w-full">
        <TableHeader>
          <DarkPoolOverviewRow removePad>
            {visibleColumns.map((t, i) => (
              <HeaderCellWrapper key={i} removePad={i + 1 === visibleColumns.length}>
                <HeaderCell>{t}</HeaderCell>
              </HeaderCellWrapper>
            ))}
          </DarkPoolOverviewRow>
        </TableHeader>
        <SingleRowBody className="flex-shrink overflow-auto px-4 py-1">
          <DarkPoolOverviewRow className="h-full items-center">
            <TableCell>${abbreviateNumber(darkPoolByTicker.premium)}</TableCell>
            <TableCell>{abbreviateNumber(darkPoolByTicker.volume)}</TableCell>
            <TableCell>{abbreviateNumber(darkPoolByTicker.lateBuyCount)}</TableCell>
            <TableCell>{abbreviateNumber(darkPoolByTicker.lateSellCount)}</TableCell>
            <TableCell>{abbreviateNumber(darkPoolByTicker.goldenTradeCount)}</TableCell>
            {!hideLevelsColumn && (
              <TableCell>
                <button
                  onClick={() => (hasAccess ? openLevels() : openSubscribe())}
                  className="flex items-center bg-transparent"
                >
                  <BsBarChartSteps className="mr-1 h-2 w-2" />{' '}
                  <span className="underline">View</span>
                </button>
              </TableCell>
            )}
          </DarkPoolOverviewRow>
        </SingleRowBody>
      </TableWrapper>
      {isLevelsOpen && <DarkPoolLevels ticker={activeTicker} onClose={closeLevels} />}
      <SubscribeModal
        isOpen={isSubscribeOpen}
        onClose={closeSubscribe}
        title="Unlock Dark Pool Level Analysis"
        buttonText="Subscribe to View Levels"
        description={
          <div className="space-y-2">
            <div className="font-semibold">
              See exactly where dark pool trades are happening with our advanced dark pool Levels
              analysis.
            </div>
            <div>
              Get detailed breakdowns of dark pool activity at different price levels to spot
              institutional support and resistance to plan your trades accordingly.
            </div>
          </div>
        }
      />
    </>
  );
};

export default TickerOverviewTable;
